import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Clock, Award, ChevronDown, Calendar, CalendarDays, Radio, User, ScanEye, Download, Mic, MapPin, Utensils } from 'lucide-react';
import CheckoutModal from '../components/CheckoutModal';
import { api } from '../lib/api';
import { downloadAgendaPdf, formatAgendaTime, groupAgendaByDay, CE_CREDIT_META, LIVE_ONLY_META } from '../lib/agenda-pdf';
import { isLiveLike, liveBadgeLabel } from '../lib/course-types';
import AgendaItemSpeaker from '../components/AgendaItemSpeaker';
import BioModal, { BioButton } from '../components/BioModal';
import MapLink from '../components/MapLink';
import { formatPromoEnd, promoModeLabel } from '../lib/promo';

export default function CourseDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === '1';
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const navigate = useNavigate();

  const storedUser = (() => {
    try { return JSON.parse(localStorage.getItem('learnable_user') || 'null'); }
    catch { return null; }
  })();
  const isLoggedIn = !!storedUser;

  useEffect(() => {
    const load = isPreview
      ? api(`/api/courses/preview/${slug}`)
      : fetch(`/api/courses/${slug}`).then((r) => r.json());
    load
      .then((d) => setCourse(d.course))
      .catch(() => setCourse(null))
      .finally(() => setLoading(false));
  }, [slug, isPreview]);

  // Tab title for in-app navigation; the server injects the same title (plus
  // share-preview tags) on a direct load — see src/server/course-meta.js.
  useEffect(() => {
    if (!course?.title) return;
    const prev = document.title;
    document.title = `${course.title} | Learnable`;
    return () => { document.title = prev; };
  }, [course?.title]);

  if (loading) return <p className="max-w-3xl mx-auto px-4 py-10 text-slate-500">Loading…</p>;
  if (!course) return <p className="max-w-3xl mx-auto px-4 py-10">Course not found.</p>;

  const price = course.price_cents === 0 ? 'Free' : `$${(course.price_cents / 100).toFixed(2)}`;
  const promoPrice = course.promo ? `$${(course.promo.special_price_cents / 100).toFixed(2)}` : null;

  // Rendered twice: above the title, and in the purchase section at the bottom.
  const buyButton = (
    <button
      onClick={() => setShowCheckout(true)}
      disabled={isPreview}
      title={isPreview ? 'Checkout is disabled in preview' : undefined}
      className="bg-brand text-white px-6 py-3 rounded-lg font-semibold hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand"
    >
      {course.price_cents === 0 ? 'Enroll' : 'Buy now'}
    </button>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      {isPreview && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-3 mb-6 text-sm">
          <ScanEye size={18} className="flex-shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">Admin preview</span> — this course is{' '}
            <span className="font-semibold">{course.status}</span>
            {course.status === 'published'
              ? '. This is how it appears to the public.'
              : ' and is not visible to the public. Checkout is disabled in preview.'}
          </div>
        </div>
      )}
      {course.hero_image_url && (
        <img src={course.hero_image_url} alt="" className="w-full rounded-lg mb-6" />
      )}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-5">
        {buyButton}
        {promoPrice ? (
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <span className="text-2xl font-bold text-red-600">{promoPrice}</span>
            <span className="text-sm text-slate-400 line-through">{price}</span>
            <span className="text-xs font-semibold text-red-700">with code <span className="font-mono tracking-wide">{course.promo.code}</span></span>
            {promoModeLabel(course.promo) && (
              <span className="text-xs font-semibold text-red-700">· {promoModeLabel(course.promo)}</span>
            )}
            {course.promo.expires_at && (
              <span className="text-xs font-semibold text-red-600">· {formatPromoEnd(course.promo.expires_at)}</span>
            )}
          </div>
        ) : (
          <span className="text-2xl font-bold">{price}</span>
        )}
      </div>
      <h1 className="text-3xl font-bold">{course.title}</h1>
      <p className="text-slate-600 mt-2">{course.short_description}</p>

      <div className="flex flex-wrap gap-5 text-sm text-slate-500 mt-3">
        {isLiveLike(course.course_type) && (
          <span className="inline-flex items-center gap-1.5 text-red-600 font-semibold">
            <Radio size={14} className="animate-pulse" /> {liveBadgeLabel(course.course_type)}
          </span>
        )}
        {isLiveLike(course.course_type) && course.live_date_utc && (
          <span className="inline-flex items-center gap-1.5 text-brand font-semibold">
            <Calendar size={14} /> {new Date(course.live_date_utc).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
        {isLiveLike(course.course_type) && course.live_date_utc && (
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <Clock size={14} /> {new Date(course.live_date_utc).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET
          </span>
        )}
        {course.course_type === 'hybrid' && course.venue_name && (
          <MapLink query={[course.venue_name, course.venue_address]} className="inline-flex items-center gap-1.5">
            <MapPin size={14} /> {course.venue_name}
          </MapLink>
        )}
        {course.duration_minutes && (
          <span className="inline-flex items-center gap-1.5">
            <Clock size={14} /> {course.duration_minutes} min
          </span>
        )}
        <span className="inline-flex items-center gap-1.5">
          <Award size={14} /> Certificate on completion
        </span>
        {course.course_type === 'hybrid' && course.lunch_included && (
          <span className="inline-flex items-center gap-1.5">
            <Utensils size={14} /> Lunch included (in person)
          </span>
        )}
      </div>

      {course.hosted_by && (
        <PresenterCard course={course} />
      )}

      {course.agenda?.length > 0 && (
        <AgendaCard course={course} />
      )}

      {course.what_youll_learn?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">What you'll learn</h3>
          <ul className="space-y-2">
            {course.what_youll_learn.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <Award size={16} className="text-brand flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {course.learning_objectives?.length > 0 && (
        <div className="mt-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">Learning Objectives</h3>
          <ul className="space-y-2">
            {course.learning_objectives.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <Award size={16} className="text-brand flex-shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {course.long_description_md && (
        <div className="mt-6 space-y-4 text-slate-700 leading-relaxed">
          {course.long_description_md
            .split(/\n\s*\n/)
            .map((para) => para.replace(/\n/g, ' ').trim())
            .filter(Boolean)
            .map((para, i) => <p key={i}>{para}</p>)}
        </div>
      )}

      <div className="mt-10 border-t pt-8">
        {course.promo && (
          <div className="inline-flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2.5 mb-4">
            <span className="text-red-600 text-lg font-extrabold">${(course.promo.special_price_cents / 100).toFixed(2)}</span>
            <span className="text-red-500 text-sm line-through">${(course.promo.original_price_cents / 100).toFixed(2)}</span>
            <span className="mx-1 text-red-300">|</span>
            <span className="text-red-700 text-sm font-semibold">Code: <span className="font-mono tracking-wide">{course.promo.code}</span></span>
            {promoModeLabel(course.promo) && (
              <>
                <span className="mx-1 text-red-300">|</span>
                <span className="text-red-700 text-xs font-semibold">{promoModeLabel(course.promo)}</span>
              </>
            )}
            {course.promo.expires_at && (
              <>
                <span className="mx-1 text-red-300">|</span>
                <span className="text-red-600 text-xs font-semibold">{formatPromoEnd(course.promo.expires_at)}</span>
              </>
            )}
          </div>
        )}
        <div className="flex items-center gap-4">
          <div className="text-3xl font-bold">{price}</div>
          {buyButton}
          {!isLoggedIn && !isPreview && (
            <Link
              to={`/login?next=/courses/${course.slug}`}
              className="text-sm text-slate-600 hover:text-slate-900 underline"
            >
              Have an account? Log in
            </Link>
          )}
        </div>
      </div>

      {showCheckout && (
        <CheckoutModal
          course={course}
          currentUser={isLoggedIn ? storedUser : null}
          onClose={() => setShowCheckout(false)}
          onSuccess={({ alreadyEnrolled }) => {
            if (alreadyEnrolled) navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
}

const SESSION_TYPE_STYLE = {
  keynote:  'bg-amber-100 text-amber-700',
  breakout: 'bg-violet-100 text-violet-700',
  break:    'bg-emerald-100 text-emerald-700',
  session:  'bg-slate-100 text-slate-600',
};

function AgendaCard({ course }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const days = groupAgendaByDay(course.agenda);
  const multiDay = days.length > 1;
  const count = course.agenda.length;

  const download = async () => {
    setDownloading(true);
    try {
      await downloadAgendaPdf(course);
    } catch {
      alert('Could not generate the agenda PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-4 border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-12 h-12 rounded-full bg-brand/15 flex items-center justify-center text-brand flex-shrink-0">
            <CalendarDays size={20} />
          </div>
          <div className="min-w-0">
            <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Event agenda</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-slate-900">
                {count} session{count === 1 ? '' : 's'}{multiDay ? ` over ${days.length} days` : ''}
              </span>
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="inline-flex items-center gap-0.5 text-[10px] font-bold text-brand hover:text-brand-dark transition-colors px-2 py-0.5 rounded-full border border-brand/30 hover:border-brand/50 hover:bg-brand/5"
              >
                {open ? 'Hide' : 'View'}
                <ChevronDown
                  size={10}
                  className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={downloading}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50 flex-shrink-0"
        >
          <Download size={13} /> {downloading ? 'Preparing…' : 'Download PDF'}
        </button>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-5 animate-fade-up">
          {days.map(({ day, label, items }) => (
            <div key={day}>
              {multiDay && (
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{label}</div>
              )}
              <ul className="space-y-3">
                {items.map((item) => (
                  <li key={item.id} className="flex flex-col sm:flex-row gap-1 sm:gap-3">
                    <div className="text-xs text-slate-500 tabular-nums sm:w-32 sm:shrink-0 sm:pt-0.5">
                      {formatAgendaTime(item) || '—'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${SESSION_TYPE_STYLE[item.session_type] || SESSION_TYPE_STYLE.session}`}>
                          {item.session_type}
                        </span>
                        {CE_CREDIT_META[item.ce_credit] && (
                          <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${CE_CREDIT_META[item.ce_credit].pill}`}>
                            {CE_CREDIT_META[item.ce_credit].label}
                          </span>
                        )}
                        {item.live_only && (
                          <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${LIVE_ONLY_META.pill}`}>
                            {LIVE_ONLY_META.label}
                          </span>
                        )}
                        <span className="text-sm font-medium text-slate-900">{item.title}</span>
                      </div>
                      <AgendaItemSpeaker item={item} venue={course} />
                      {item.description && <p className="text-xs text-slate-600 mt-1 leading-relaxed">{item.description}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PresenterCard({ course }) {
  const [bioOpen, setBioOpen] = useState(false);

  return (
    <div className="mt-6 border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-3">
        {course.hosted_by_image_url ? (
          <img
            src={course.hosted_by_image_url}
            alt={course.hosted_by}
            className="w-12 h-12 rounded-full object-cover border border-slate-200"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-brand/15 flex items-center justify-center text-brand">
            <User size={20} />
          </div>
        )}
        <div className="min-w-0">
          <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">Hosted by</div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-slate-900">
              {course.hosted_by}{course.hosted_by_title ? `, ${course.hosted_by_title}` : ''}
            </span>
            {course.hosted_by_bio && <BioButton onClick={() => setBioOpen(true)} />}
          </div>
        </div>
      </div>
      {bioOpen && course.hosted_by_bio && (
        <BioModal
          name={course.hosted_by}
          title={course.hosted_by_title}
          imageUrl={course.hosted_by_image_url}
          bio={course.hosted_by_bio}
          onClose={() => setBioOpen(false)}
        />
      )}
    </div>
  );
}
